import { RulesLogic } from 'json-logic-js';

import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { CombinedStepType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  ComponentJsonLogicKey,
  LogicRuleSection,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/types';
import { ComponentLibrarySupportedType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import {
  ClientStringKeys,
  ExtractKeysOfType,
  TranslationKeys,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/types';
import {
  CombinedStepConfig,
  InquiryTemplateVersionStepType,
  InquiryTemplateVersionStepUi,
} from 'dashboard/models/inquiry-template-version';
import { FieldSchemaType } from 'lib/fields';
import { FeatureFlag } from 'lib/interfaces/feature-flags';
import {
  GovernmentIdTypeSelectComponentConfig,
  Option as ComponentOption,
  StepperComponentConfig,
  StepperStepConfig,
  UiComponent,
  UiComponentConfig,
  UiComponentConfigType,
} from 'lib/interfaces/ui-components';

export type DraggableHoverPosition = 'top' | 'bottom' | 'left' | 'right' | null;

export enum ComponentLibrarySection {
  BASIC = 'Basic',
  FORMS = 'Forms',
}

interface BaseScreenEditorComponent {
  displayName: string;
  featureFlag?: {
    flag: FeatureFlag;
    renderValue: boolean;
  };
}

export interface ComponentLibraryScreenEditorComponent extends BaseScreenEditorComponent {
  componentLibrary: {
    icon: React.ReactNode;
    section: ComponentLibrarySection;
    hideUiComponentDragPreview?: boolean;
  };
}

export type ScreenEditorComponent =
  | BaseScreenEditorComponent
  | ComponentLibraryScreenEditorComponent;

export enum ScreenEditorDraggableItemTypes {
  COMPONENT = 'component',
}

export type SelectedComponentAndStep = {
  component: UiComponentConfig;
  step: InquiryTemplateVersionStepUi;
  error: boolean;
  newComponent?: boolean;
} | null;

export interface ScreenEditorComponentItem
  extends Omit<Extract<UiComponentConfig, { type: ComponentLibrarySupportedType }>, 'name'> {
  stepName?: string;
  name?: string;
}

// Schema definitions that have to be defined on the client
// Generating component definitions on the server will fail validations
const CLIENT_ONLY_SCHEMA_TYPES = [
  UiComponentConfigType.ImageHosted,
  UiComponentConfigType.Stepper,
] as const;

type ClientOnlyComponentConfigTypes = Extract<
  UiComponentConfigType,
  (typeof CLIENT_ONLY_SCHEMA_TYPES)[number]
>;

export const isClientDefinedComponentSchema = (
  componentConfig: UiComponentConfigType,
): componentConfig is ClientOnlyComponentConfigTypes =>
  CLIENT_ONLY_SCHEMA_TYPES.some((type) => type === componentConfig);

export type ClientOnlyComponentSchemaRecord = Partial<{
  [ConfigType in ClientOnlyComponentConfigTypes]: {
    config: Omit<Extract<UiComponentConfig, { type: ConfigType }>, 'name'>;
    preview: Record<string, never>;
  };
}>;

export const isScreenComponentInComponentLibrary = (
  screenEditorData: ScreenEditorComponent,
): screenEditorData is ComponentLibraryScreenEditorComponent => {
  return 'componentLibrary' in screenEditorData === true;
};

export type EditorComponentSchemaRecord<Config extends UiComponentConfig = UiComponentConfig> =
  Partial<Record<UiComponentConfigType, EditorComponentSchema<Config>>>;
export type EditorCombinedStepSchemaRecord = Record<CombinedStepType, EditorCombinedStepSchema>;

export enum ComponentSettingType {
  Select = 'select',
  MultiSelect = 'multiselect',
  Transition = 'transition',
  Image = 'image',
  Number = 'number',
  Toggle = 'toggle',
  Radio = 'radio',
  DateRange = 'date_range',
  Text = 'text',
  Config = 'config',
}

export type KeysOfUnion<T> = T extends T ? keyof T : never;
interface ComponentSettingBase<
  Config extends UiComponentConfig = UiComponentConfig,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ClientKeyType = any,
> {
  'client-key': ExtractKeysOfType<Config['config'], ClientKeyType>;
  label: string;
  'helper-text': string;
  hidden: RulesLogic | null;
  placeholder?: string;
}

export interface StringOptionType {
  label: string;
  value: string;
}

export interface ObjectOptionType {
  label: string;
  value: object;
}

export type GroupedStringOptionType = StringOptionType & { 'group-label': string };
export type GroupedObjectOptionType = ObjectOptionType & { 'group-label': string };

type OptionType =
  | StringOptionType[]
  | ObjectOptionType[]
  | GroupedStringOptionType[]
  | GroupedObjectOptionType[];

export enum DynamicOptionObjectTypes {
  Step = 'step',
  Field = 'field',
}

export interface StepDynamicOption {
  'object-type': DynamicOptionObjectTypes.Step;
  'step-types': InquiryTemplateVersionStepType[];
}

interface FieldDynamicOption {
  'object-type': DynamicOptionObjectTypes.Field;
  'field-type': FieldSchemaType;
}

export type DynamicOption = StepDynamicOption | FieldDynamicOption;

export type ComponentSettingSelect<Config extends UiComponentConfig = UiComponentConfig> =
  ComponentSettingBase<Config, Array<ComponentOption>> & {
    type: ComponentSettingType.Select;
    nullable: boolean;
  } & (
      | {
          options: undefined;
          'dynamic-option': DynamicOption;
        }
      | {
          options: OptionType;
          'dynamic-option': undefined;
        }
    );

export type ComponentSettingMultiSelect<Config extends UiComponentConfig = UiComponentConfig> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ComponentSettingBase<Config, any[]> & {
    type: ComponentSettingType.MultiSelect;
    'select-all-enabled': boolean;
    'search-enabled': boolean;
  } & (
      | {
          options: undefined;
          'dynamic-option': DynamicOption;
        }
      | {
          options: OptionType;
          'dynamic-option': undefined;
        }
    );

export interface ComponentSettingRadio<Config extends UiComponentConfig = UiComponentConfig>
  extends ComponentSettingBase<Config> {
  type: ComponentSettingType.Radio;
  options: StringOptionType[];
}

export enum NumberConversionType {
  BytesToMb = 'bytes_to_mb',
}

export interface ComponentSettingNumber<Config extends UiComponentConfig = UiComponentConfig>
  extends ComponentSettingBase<Config> {
  type: ComponentSettingType.Number;
  min: number | null;
  max: number | null;
  'default-value': number;
  conversion: NumberConversionType | null;
}

export interface ComponentSettingTransition {
  type: ComponentSettingType.Transition;
}

export interface ComponentSettingDateRange {
  type: ComponentSettingType.DateRange;
  label: string;
  hidden: RulesLogic | null;
}

export interface ComponentSettingImage<Config extends UiComponentConfig = UiComponentConfig>
  extends ComponentSettingBase<Config, string> {
  type: ComponentSettingType.Image;
  'local-image-key': 'image-key';
  'alt-key': ExtractKeysOfType<Config['config'], string>;
  nullable: boolean;
}

export interface ComponentSettingText<Config extends UiComponentConfig = UiComponentConfig>
  extends ComponentSettingBase<Config, string> {
  type: ComponentSettingType.Text;
}

export interface ComponentSettingToggle<Config extends UiComponentConfig = UiComponentConfig>
  extends ComponentSettingBase<Config, boolean> {
  type: ComponentSettingType.Toggle;
}

type NestedConfigTypes = StepperStepConfig;

export interface ComponentSettingConfig<Config extends UiComponentConfig = UiComponentConfig>
  extends ComponentSettingBase<Config, NestedConfigTypes[]> {
  type: ComponentSettingType.Config;
  'empty-config': NestedConfigTypes;
  settings: ComponentSetting<Config>[];
  translations: ComponentTranslationsSchema<NestedConfigTypes>[];
  'config-label': string;
}

export type ComponentSetting<Config extends UiComponentConfig = UiComponentConfig> =
  | ComponentSettingSelect<Config>
  | ComponentSettingImage<Config>
  | ComponentSettingTransition
  | ComponentSettingNumber<Config>
  | ComponentSettingToggle<Config>
  | ComponentSettingRadio<Config>
  | ComponentSettingDateRange
  | ComponentSettingMultiSelect<Config>
  | ComponentSettingText<Config>
  | ComponentSettingConfig<Config>;

type Unnest<T> = {
  [K in keyof T]: T[K] extends object ? T[K] : never;
}[keyof T];

interface BaseTranslationsSchema<
  Config extends UiComponentConfig | NestedConfigTypes | CombinedStepConfig['localizations'],
> {
  label: string;
  'custom-translations-key': TranslationKeys<
    Config extends UiComponentConfig
      ? Config['config']
      : Config extends CombinedStepConfig['localizations']
        ? Unnest<Config>
        : Config
  >;
}

export interface ComponentTranslationsSchema<
  Config extends UiComponentConfig | NestedConfigTypes = UiComponentConfig,
> extends BaseTranslationsSchema<Config> {
  'client-key': ClientStringKeys<UiComponent['attributes']>;
  'component-preview-name': string | undefined;
}

export interface CombinedStepTranslationsSchema<
  Config extends CombinedStepConfig['localizations'] = CombinedStepConfig['localizations'],
> extends BaseTranslationsSchema<Config> {
  'client-key': string; // NOTE: tighter typing for inferrence from CombinedStepClientConfig
}

export type EditorCombinedStepSchema = {
  translations: Partial<
    Record<keyof CombinedStepConfig['localizations'], CombinedStepTranslationsSchema[]>
  >;
};

export interface EditorComponentSchema<Config extends UiComponentConfig = UiComponentConfig> {
  properties: {
    'refresh-preview-attributes': KeysOfUnion<UiComponentConfig['config']>[];
    translations: ComponentTranslationsSchema<Config>[];
    settings: ComponentSetting<Config>[];
  };
  'required-access-tier': EditorAccessTier | undefined;
  styles: string[];
  logic: {
    'client-key': ComponentJsonLogicKey;
    'evaluation-context': LogicRuleSection;
    namespace: undefined | KeysOfUnion<UiComponentConfig['config']>;
    'rule-name': string;
    'inverted-rule-name': string;
  }[];
}

// Components derive their children in preview from some other data source than the
// `children` attribute within the config or component preview data
type ComposedUiComponentConfig = StepperComponentConfig | GovernmentIdTypeSelectComponentConfig;

export const isComposedUiComponentConfig = (
  componentConfig: UiComponentConfig | undefined,
): componentConfig is ComposedUiComponentConfig => {
  switch (componentConfig?.type) {
    case UiComponentConfigType.Stepper:
    case UiComponentConfigType.GovernmentIdTypeSelect:
      return true;
    default:
      return false;
  }
};

export enum SelectedComponentTab {
  PROPERTIES = 'properties',
  STYLES = 'styles',
  FIELD = 'field',
  LOGIC = 'logic',
}

export const isSelectedComponentTab = (tab: string): tab is SelectedComponentTab => {
  return Object.values(SelectedComponentTab).includes(tab as SelectedComponentTab);
};
